<template>
  <app-navigation-rail :active-transfers="activeTransfers">
    <template #bottom-extra>
      <!-- The caller's in-app feed: unread lines counted on an interval, the newest on a click.
           It and the balance below are keyed on the organization like the router-view, and for
           its reason: they outlive the switch, and a mounted query is not told of the clear. -->
      <notification-bell :key="orgKey" variant="rail" />
      <!-- The balance, for billing managers on a platform that sells credits (U5). -->
      <credits-chip :key="orgKey"/>
      <!-- Shown only when there is something to open: the dock signals, this is
           the way back to it once the dock has gone. -->
      <v-tooltip v-if="transfersCount > 0" location="end" :open-delay="500">
        <template #activator="{ props }">
          <div v-bind="props" class="transfers-nav-item" @click="openTransfers">
            <div class="nav-btn" :class="{ 'icon-active': uploadStore.drawerOpen }">
              <v-badge
                :content="transfersCount"
                :model-value="transfersCount > 0"
                color="primary"
                offset-x="-2"
                offset-y="-2"
              >
                <v-icon :icon="transfersIcon" size="22" class="custom-icon" />
              </v-badge>
            </div>
            <div class="app-title">{{ $t('assets.transfers.drawer.title') }}</div>
          </div>
        </template>
        <span class="text-caption">{{ $t('assets.transfers.drawer.title') }}</span>
      </v-tooltip>
    </template>
  </app-navigation-rail>
  <!-- The account menu: a drawer beside the rail, Plus's sheet on a phone, where it also holds
       the overflow apps and what the rail's bottom holds (docs/plans/mobile-navigation.md MN3). -->
  <component
    :is="mobile ? MoreSheet : UserDrawer"
    :shortcuts="shellShortcuts"
    :user-display-name="session.userName"
    :user-email="session.user?.email"
    @sign-out="handleSignOut"
    @help="openHelpCenter"
    @invite="openOrgInviteModal"
    @create-organization="openFoundingModal"
  >
    <template #notifications="{ close }">
      <notification-bell :key="orgKey" variant="icon" @navigate="close" />
    </template>
    <!-- On a phone the transfers are always listed here, empty or not: no swipe opens their
         drawer, and the sheet is the one way to reach it, past exports included. -->
    <template v-if="mobile" #default="{ close }">
      <v-list-item slim @click="close(); openTransfers()">
        <template #prepend>
          <v-icon :icon="transfersIcon" size="20" />
        </template>
        <v-list-item-title>{{ $t('assets.transfers.drawer.title') }}</v-list-item-title>
        <template v-if="transfersCount > 0" #append>
          <v-badge :content="transfersCount" color="primary" inline />
        </template>
      </v-list-item>
      <credits-chip variant="row" @navigate="close"/>
    </template>
  </component>
  <upload-drawer location="right"/>

  <v-main>
    <!-- The wallet's warnings, above whatever screen is open: a billing manager's with their
         remedy, and every other member's neutral one at an empty balance. -->
    <balance-banners :key="orgKey"/>
    <!-- Keyed on the organization: the session guard clears every cached read when the tenant
         changes, and a mounted query is not told — so a view the switch would otherwise reuse
         (home to home) kept the organization it was leaving on screen. -->
    <router-view :key="orgKey"/>
  </v-main>

  <upload-mini-dock/>
  <shared-asset-player/>
  <floating-mini-player/>
  <toast-container/>
  <modal-container/>
</template>

<script setup lang="ts">
import {computed, onMounted, watch} from 'vue'
import { useDisplay } from 'vuetify'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { mdiSwapVertical, mdiCloudUpload, mdiDownload } from '@mdi/js'
import {
  AppNavigationRail,
  MoreSheet,
  UserDrawer,
  ToastContainer,
  ModalContainer,
  useThemeStore,
  useLocaleStore,
} from '@/apps/core'
import { useQueryClient } from '@tanstack/vue-query'
import { videasPageUrl } from '@/videasLinks'
import { useSessionStore } from '@/apps/session/stores/session'
import { useOrgInviteModal } from '@/apps/settings/composables/useOrgInviteModal'
import { useFoundingModal } from '@/apps/session/composables/useFoundingModal'
import {UploadDrawer, UploadMiniDock} from '@/apps/assets/components/upload'
import {useUploadStore} from '@/apps/assets/stores/useUploadStore'
import {useExportJobsStore} from '@/apps/assets/stores/useExportJobsStore'
import FloatingMiniPlayer from '@/apps/assets/components/asset-detail/FloatingMiniPlayer.vue'
import SharedAssetPlayer from '@/apps/assets/components/asset-detail/SharedAssetPlayer.vue'
import {useUploadCacheInvalidation} from '@/apps/assets/composables/useUploadCacheInvalidation'
import { shellShortcuts } from '@/layouts/shell'
import NotificationBell from '@/apps/notifications/components/NotificationBell.vue'
import BalanceBanners from '@/apps/billing/components/BalanceBanners.vue'
import CreditsChip from '@/apps/billing/components/CreditsChip.vue'

const router = useRouter()
const { mobile } = useDisplay()
const route = useRoute()
// What every org-scoped part of the shell is keyed on, so the switch remounts it.
const orgKey = computed(() => String(route.params.organizationId))
const queryClient = useQueryClient()
const { locale } = useI18n()
const session = useSessionStore()
const themeStore = useThemeStore()
const localeStore = useLocaleStore()
const uploadStore = useUploadStore()
const exportJobs = useExportJobsStore()
// What the drawer's "invite" and "create an organization" entries open.
const { openOrgInviteModal } = useOrgInviteModal()
const { openFoundingModal } = useFoundingModal()

const transfersCount = computed(() => uploadStore.uploadList.length + exportJobs.jobs.length)
// What Plus's badge counts on a phone, where it stands in for the transfers dock: uploads and
// exports still running, and the exports that ended and wait on the user (ready to download,
// or failed) — not the finished ones the drawer keeps listing.
const activeTransfers = computed(
  () =>
    uploadStore.activeUploads.length +
    uploadStore.pendingUploads.length +
    exportJobs.activeCount +
    exportJobs.readyPending.length +
    exportJobs.failedPending.length,
)
const transfersIcon = computed(() => {
  if (uploadStore.hasActiveOrPendingUploads) return mdiCloudUpload
  if (exportJobs.activeCount > 0) return mdiDownload
  return mdiSwapVertical
})

function openTransfers() {
  const exportsWaiting =
    exportJobs.activeCount + exportJobs.readyPending.length + exportJobs.failedPending.length > 0
  uploadStore.drawerTab =
    exportsWaiting && !uploadStore.hasActiveOrPendingUploads ? 'exports' : 'uploads'
  uploadStore.openDrawer()
}

async function handleSignOut() {
  // Forgotten here whether or not the server answered, and every cached read with it.
  await session.logout().catch(() => {})
  queryClient.clear()
  await router.replace({ name: 'session:login' })
}

// The footer "Aide" link opens the public help centre, in the user's language.
function openHelpCenter() {
  window.open(videasPageUrl('helpCenter', locale.value), '_blank', 'noopener,noreferrer')
}

// Invalidate asset caches when uploads complete
useUploadCacheInvalidation()

onMounted(() => {
  themeStore.initTheme()
  localeStore.initLocale()
})

// Sync app language with the account's preferred locale
watch(
  () => session.user?.locale,
  (language) => {
    if (language) {
      localeStore.syncFromUserLanguage(language)
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

// The mini player's bar sits above the bottom bar on a phone, and the creation button above
// both: the page scrolls clear of them too. Each is 0 when absent and above the phone tier.
.v-main {
  padding-bottom: calc(var(--v-layout-bottom) + var(--app-mini-player-h) + var(--app-fab-clearance));

  // A screen that fills the height takes what the wallet's banners leave, where 100 % of `v-main`
  // put its bottom below the window by their height. Its root says which it is: `h-100` when the
  // document scrolls, `min-content` keeping a taller one growing with its content, and
  // `fits-window` when it scrolls inside itself. Only such a screen makes `v-main` a column: in
  // one, a root on auto margins would shrink to its content.
  &:has(> .h-100, > .fits-window) {
    display: flex;
    flex-direction: column;
  }

  > :deep(.h-100) {
    flex: 1 1 0;
    min-height: min-content;
  }

  > :deep(.fits-window) {
    flex: 1 1 0;
    min-height: 0;
  }

  // On a phone the bottom bar also covers the iPhone's gesture bar, which the layout size Vuetify
  // hands `v-main` leaves out: the page stands on the inset itself, which counts it (MN4).
  @include phone {
    padding-bottom: calc(var(--app-bottom-inset) + var(--app-fab-clearance));
  }
}

.transfers-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  cursor: pointer;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    .nav-btn:not(.icon-active) .custom-icon {
      color: rgba(var(--v-theme-on-surface), 0.87);
    }

    .app-title {
      color: rgba(var(--v-theme-on-surface), 0.95);
    }
  }

  .nav-btn {
    transition: all 0.2s ease;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--v-theme-surface), 1);

    &.icon-active {
      background-color: rgba(var(--v-theme-primary), 0.08);

      .custom-icon {
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  .custom-icon {
    color: rgba(var(--v-theme-on-surface), 0.75);
    transition: color 0.2s ease;
  }

  .app-title {
    letter-spacing: 0.02em;
    font-size: 0.625rem;
    color: rgba(var(--v-theme-on-surface), 0.75);
    margin-top: 0.25rem;
    font-weight: 500;
    transition: color 0.2s ease;
  }
}
</style>
